import { ACTIVITY_LOG_EXPORT } from './constants';

/**
 * IANA zone of this browser. Same source as notification schedule payloads (`timezone`).
 * @returns {string}
 */
export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/**
 * @param {unknown} value
 * @returns {Array}
 */
function asExportList(value) {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.exports)) return value.exports;
  if (value && Array.isArray(value.items)) return value.items;
  return [];
}

/**
 * Open the signed URL returned by GET /v2/export/:exportId/file.
 * @param {string} url
 * @param {string} [fileName]
 */
export function openSignedExportUrl(url, fileName) {
  if (!url) return;
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  if (fileName) link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * @param {Object} raw
 * @returns {Object}
 */
export function normalizeExportRow(raw) {
  const source = raw || {};
  const file = source.file || (source.payload && source.payload.file) || null;
  const exportType = source.exportType || source.scope || (source.payload && source.payload.scope) || ACTIVITY_LOG_EXPORT.EXPORT_TYPE_ACTIVITY_LOG;
  return {
    exportId: source.exportId || source._id || '',
    stage: Number(source.stage),
    requestedAt: source.requestedAt || source.created_at || source.createdAt || '',
    exportType,
    fileName: (file && file.fileName) || '',
    storageLocation: (file && file.storageLocation) || '',
    error: source.error || '',
  };
}

/**
 * @param {unknown} payload
 * @returns {Array}
 */
export function normalizeExportHistory(payload) {
  return asExportList(payload)
    .map(normalizeExportRow)
    .filter((row) => row.exportId)
    .sort((a, b) => new Date(b.requestedAt || 0) - new Date(a.requestedAt || 0));
}

/**
 * Keep local rows the list has not returned yet (accept can land before the history read).
 * @param {Array} serverRows
 * @param {Array} localRows
 * @returns {Array}
 */
export function mergeExportHistory(serverRows, localRows) {
  const seen = new Set((serverRows || []).map((row) => row.exportId));
  const pending = (localRows || []).filter((row) => row.exportId && !seen.has(row.exportId));
  return pending.concat(serverRows || []);
}

/**
 * @param {number} stage
 * @returns {boolean}
 */
export function isGeneratingStage(stage) {
  return stage === ACTIVITY_LOG_EXPORT.STAGE_QUEUED || stage === ACTIVITY_LOG_EXPORT.STAGE_IN_PROGRESS;
}

/**
 * @param {number} stage
 * @returns {boolean}
 */
export function isReadyStage(stage) {
  return stage === ACTIVITY_LOG_EXPORT.STAGE_FINISHED;
}

/**
 * @param {number} stage
 * @returns {boolean}
 */
export function isFailedStage(stage) {
  return stage === ACTIVITY_LOG_EXPORT.STAGE_FAILED;
}

/**
 * @param {number} stage
 * @returns {string}
 */
export function exportStatusLabel(stage) {
  if (isGeneratingStage(stage)) return ACTIVITY_LOG_EXPORT.STATUS_GENERATING;
  if (isReadyStage(stage)) return ACTIVITY_LOG_EXPORT.STATUS_READY;
  if (isFailedStage(stage)) return ACTIVITY_LOG_EXPORT.STATUS_FAILED;
  return '';
}

/**
 * Row shape consumed by the Export Manager datatable filters.
 * @param {Object} row
 * @returns {Object}
 */
export function toExportTableRow(row) {
  const source = row || {};
  return {
    ...source,
    exportTypeLabel: exportTypeLabel(source.exportType),
    statusLabel: exportStatusLabel(source.stage),
  };
}

/**
 * @param {string} exportType
 * @returns {string}
 */
export function exportTypeLabel(exportType) {
  if (exportType === ACTIVITY_LOG_EXPORT.EXPORT_TYPE_FILE || exportType === 'File') {
    return ACTIVITY_LOG_EXPORT.LABEL_FILE;
  }
  if (
    exportType === ACTIVITY_LOG_EXPORT.EXPORT_TYPE_ACTIVITY_LOG
    || exportType === ACTIVITY_LOG_EXPORT.SCOPE_CYCLE
    || exportType === 'Activity log'
  ) {
    return ACTIVITY_LOG_EXPORT.LABEL_ACTIVITY_LOG;
  }
  return exportType || ACTIVITY_LOG_EXPORT.LABEL_ACTIVITY_LOG;
}
